import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentUser, useUpdateUser } from 'services/repositories/user/user';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { FilterValue } from 'support/types/filters';

// Reads and persists the user's default list filters for a given page key. Storage is per-user
// (UserSettings.default_filters), reached through the existing user-update path; the backend keeps
// a generic `{ [pageKey]: FilterValue[] }` map, so any list page can opt in with its own key.
export const useDefaultFilters = (pageKey: string) => {
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();
  const { mutateAsync: updateCurrentUser, isLoading: isSaving } = useUpdateUser();

  const defaultFilterValues = currentUser?.defaultFilters?.[pageKey];

  const saveAsDefault = useCallback(
    async (filterValues: Array<FilterValue>) => {
      if (!currentUser) {
        return;
      }

      try {
        await updateCurrentUser({
          name: currentUser.name,
          email: currentUser.email,
          language: currentUser.language,
          notifications: currentUser.notifications,
          default_filters: { ...(currentUser.defaultFilters ?? {}), [pageKey]: filterValues },
        });
        addNotification(t('common:components.filters.defaultFiltersSaved'));
      } catch (_error) {
        addNotification(t('auth:notifications.updateUser.error'), NotificationType.error);
      }
    },
    [currentUser, pageKey, updateCurrentUser, t],
  );

  return { defaultFilterValues, saveAsDefault, isSaving };
};
